import * as React from "react";
import type { Root } from "react-dom/client";
import { createRoot } from "react-dom/client";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IKeytipProps, ITheme } from "@fluentui/react";
import { DirectionalHint } from "@fluentui/react/lib/ContextualMenu";
import { getRTL, memoizeFunction } from "@fluentui/react/lib/Utilities";

import type { AppColors } from "@1js/acui-common";
import {
  commonCalloutBoxShadow,
  getActionControlSemanticColorsWithTheme,
  InputMethod,
} from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { iconNameToIconProps } from "@1js/acui-icons";
import type { IPivotHeader, PivotHeaderExecutePayload } from "@1js/acui-pivot";
import {
  Pivot,
  pivotHeaderLabelClassname,
  pivotHeaderWrapperIDSuffix,
  SelectedPivotIndicator,
} from "@1js/acui-pivot";
import { AppThemeConsumer, isThemingEnabled } from "@1js/acui-theme";
import { getBoundingWindowWithPaddingFabric } from "@1js/window-util";

import { getRibbonBottomBarAriaControlsId } from "../Ribbon/Ribbon.Props";
import { ConnectedTabState } from "./ConnectedTabState";
import {
  getOverflowMenuClassNames,
  getOverflowMenuItemClassNames,
} from "./TabList.classNames";
import type { TabHeader, TabListProps } from "./TabList.Props";
import {
  getContextualTabHeaderStyles,
  getPivotStyles,
  getTabHeaderStyles,
} from "./TabList.styles";
import { getRibbonTopBarStylingConstants } from "../../ribbonStylingConstants";
import type {
  CoordinateMap,
  PivotIndicatorDOMCoordinates,
} from "./TabList.types";

const getPivotHeaders = memoizeFunction(
  (
    tabHeaders: TabHeader[],
    theme: ITheme,
    appColors: AppColors,
    selectedTabId: string,
    onFocus?: () => void,
    useNormalStyleForContextualTabs?: boolean,
    useNextStyles?: boolean
  ): IPivotHeader[] => {
    return tabHeaders.map((item, index) => {
      let pivotHeaderStyles;
      const isContextual = item.tabType === "contextual";
      const previousTab: TabHeader | undefined =
        index > 0 ? tabHeaders[index - 1] : undefined;
      const nextTab: TabHeader | undefined =
        index + 1 < tabHeaders.length ? tabHeaders[index + 1] : undefined;
      const isPrevTabContextual =
        previousTab && previousTab.tabType === "contextual";
      // a tab is "connected" if previousTab.connectedTabId === currentTab.id
      const isConnectedToPreviousTab =
        isContextual &&
        isPrevTabContextual &&
        previousTab!.connectedTabId === item.id;
      const isConnectedToNextTab =
        isContextual &&
        nextTab &&
        nextTab.tabType === "contextual" &&
        item.connectedTabId === nextTab.id;

      const connectedTabState = isConnectedToPreviousTab
        ? ConnectedTabState.Last
        : isConnectedToNextTab
          ? ConnectedTabState.First
          : ConnectedTabState.None;

      // we display a vertical separator between any two adjacent contextual tabs unless the two contextual tabs are connected to each other
      // in this case, we do NOT put a separator between them
      // connected means a given tab id is equal to the .connectedTabId of the tab preceding it
      const displayTabVerticalSeparator =
        isContextual && isPrevTabContextual && !isConnectedToPreviousTab;
      if (isContextual && !useNormalStyleForContextualTabs) {
        pivotHeaderStyles = getContextualTabHeaderStyles(
          theme,
          appColors,
          getRibbonTopBarStylingConstants().tabHeaderHeight,
          connectedTabState,
          useNextStyles
        );
      } else {
        pivotHeaderStyles = getTabHeaderStyles(
          theme,
          getRibbonTopBarStylingConstants().tabHeaderHeight,
          item.styles,
          useNextStyles
        );
      }

      return {
        ...item,
        styles: pivotHeaderStyles,
        displayTabVerticalSeparator,
        getMenuItemClassNames: getOverflowMenuItemClassNames(appColors),
        ariaControls:
          selectedTabId === item.id
            ? getRibbonBottomBarAriaControlsId(item.id)
            : undefined,
        onFocus,
      };
    });
  }
);

export class TabList extends React.PureComponent<TabListProps, {}> {
  private pivot = React.createRef<Pivot>();
  private pivotBarWrapper = React.createRef<HTMLDivElement>();
  private pivotWrapper = React.createRef<HTMLDivElement>();
  private pivotBarWrapperRoot: Root | undefined = undefined;
  private selectedPivotIndicatorAnimate: boolean = false;
  private headerCoordinates: CoordinateMap = {};
  private positionOffset: number = 0;
  private noOpFileMenuOpenId: string = "noOpFileMenuOpenId";
  private pivotIndicatorLocationId: string = "";

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public render() {
    const {
      appColors,
      topLevelTabHeaders,
      overflowTabHeaders,
      label,
      selectedTabId,
      overflowTabHeaderMenuLauncherIcon,
      keytipProps,
      overflowMenuLauncherLabel,
      onFocus,
      displayDynamicTabHeaderIndicator,
      automationType,
      useNormalStyleForContextualTabs,
      useNextStyles,
      hideSelectedTabIndicators,
      customStyles,
    } = this.props;

    const focusZoneProps = {
      ...this.props.focusZoneProps,
      onKeyDown: (ev: React.KeyboardEvent<HTMLDivElement>) => {
        // Prevent Ctrl+Arrow navigation in v8 from being handled by the tab list itself.
        // This allows the event to bubble up to the RibbonContainer, where Ctrl+Arrow
        // is used to move focus between the top and bottom bars instead of between tabs.
        if (ev.ctrlKey) {
          ev.preventDefault();
          return;
        }
      },
    };

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const semanticColors = getActionControlSemanticColorsWithTheme(theme);
          const pivotStyles = getPivotStyles(
            theme,
            useNextStyles,
            customStyles?.pivotStyles
          );
          return (
            <span
              ref={this.pivotWrapper}
              style={{
                display: "inline-block",
              }}
            >
              <Pivot
                componentRef={this.pivot}
                label={label}
                selectedPivotId={selectedTabId}
                topLevelItems={getPivotHeaders(
                  topLevelTabHeaders,
                  theme,
                  appColors,
                  selectedTabId,
                  onFocus,
                  useNormalStyleForContextualTabs,
                  useNextStyles
                )}
                overflowItems={
                  overflowTabHeaders &&
                  getPivotHeaders(
                    overflowTabHeaders,
                    theme,
                    appColors,
                    selectedTabId,
                    onFocus,
                    useNormalStyleForContextualTabs,
                    useNextStyles
                  )
                }
                onPivotHeaderExecute={this.getOnTabHeaderActivate}
                onPivotHeaderMouseEnter={
                  this.getFullWidthIndicatorMouseEventHandler
                }
                onPivotHeaderMouseLeave={this.getMouseEventHandler}
                onPivotHeaderMouseDown={this.getMouseEventHandler}
                onPivotHeaderMouseUp={
                  this.getFullWidthIndicatorMouseEventHandler
                }
                onPivotHeaderClick={this.onClick}
                onPivotHeaderFocus={this.getOnTabHeaderFocus}
                onOverflowButtonExecute={this.onOverflowButtonExecute}
                styles={pivotStyles}
                menuProps={{
                  isBeakVisible: false,
                  directionalHint: DirectionalHint.bottomRightEdge,
                  getMenuClassNames: getOverflowMenuClassNames(
                    appColors,
                    this.context.responsiveClassName
                  ),
                  calloutProps: {
                    styles: {
                      root: commonCalloutBoxShadow,
                    },
                    bounds: getBoundingWindowWithPaddingFabric,
                  },
                }}
                menuIconProps={iconNameToIconProps(
                  overflowTabHeaderMenuLauncherIcon,
                  isThemingEnabled() ? semanticColors.textColor : "#444444"
                )}
                appColors={appColors}
                keytipProps={keytipProps as IKeytipProps}
                overflowMenuLauncherLabel={overflowMenuLauncherLabel}
                displayTabIndicatorPlaceholder={
                  !displayDynamicTabHeaderIndicator &&
                  !hideSelectedTabIndicators
                }
                focusZoneProps={focusZoneProps}
                automationType={automationType}
                useNextStyles={useNextStyles}
                hideSelectedTabIndicators={hideSelectedTabIndicators}
              />
              {displayDynamicTabHeaderIndicator &&
                !hideSelectedTabIndicators && (
                  <div ref={this.pivotBarWrapper} />
                )}
            </span>
          );
        }}
      </AppThemeConsumer>
    );
  }

  public componentDidMount() {
    if (this.pivotBarWrapper.current) {
      this.pivotBarWrapperRoot = createRoot(this.pivotBarWrapper.current);
      this.setPivotHeaderCoordinates();
      this.renderPivotIndicator(this.props.selectedTabId, false);
    }
  }

  public componentWillUnmount() {
    if (this.pivotBarWrapper.current && this.pivotBarWrapperRoot) {
      this.pivotBarWrapperRoot.unmount();
    }
  }

  public componentDidUpdate(prevProps: TabListProps) {
    if (
      this.pivotBarWrapper.current &&
      (this.overflowItemsChanged(
        prevProps.overflowTabHeaders || [],
        this.props.overflowTabHeaders || []
      ) ||
        this.topLevelHeaderItemsChanged(
          prevProps.topLevelTabHeaders,
          this.props.topLevelTabHeaders
        ) ||
        this.pivotIndicatorLocationId !== this.props.selectedTabId ||
        this.props.hideSelectedTabIndicators !==
          prevProps.hideSelectedTabIndicators)
    ) {
      // Don't animate if we are updating due to a scaling step
      this.setPivotHeaderCoordinates();
      this.selectedPivotIndicatorAnimate =
        prevProps.selectedTabId !== this.props.selectedTabId;
      this.onPivotHeaderChanged(this.props.selectedTabId, false);
    }
  }

  /**
   * The SelectedPivotIndicator relies on the positioning of the pivots. In the event that the pivots
   * change position (scaling, or selecting a tab from the overflow menu), we need to ensure that the
   * pivots are repositioned BEFORE the SelectedPivotIndicator reads from those positions. This is why
   * we have to render this component after the primary update.
   */
  private renderPivotIndicator(id: string, fullLength: boolean) {
    if (this.pivotBarWrapper.current) {
      const { isTabIndicatorRounded } = this.props;
      this.pivotIndicatorLocationId = id;
      const coordinates = this.getCachedCoordinateValues(id, fullLength);

      if (!this.pivotBarWrapperRoot) {
        this.pivotBarWrapperRoot = createRoot(this.pivotBarWrapper.current);
      }
      this.pivotBarWrapperRoot.render(
        <SelectedPivotIndicator
          appColors={this.props.appColors}
          position={coordinates.position}
          length={coordinates.length}
          showAnimation={this.selectedPivotIndicatorAnimate}
          useNextStyles={this.props.useNextStyles}
          isTabIndicatorRounded={isTabIndicatorRounded}
        />
      );
    }
    this.selectedPivotIndicatorAnimate = true;
  }

  /**
   * Returns true if the overflow items have changed. This is the scenario where we need to re-render
   * the pivot indicator AFTER the Pivot has finished updating.
   */
  private overflowItemsChanged(
    prevOverflowItems: IPivotHeader[],
    currentOverflowItems: IPivotHeader[]
  ): boolean {
    if (prevOverflowItems.length !== currentOverflowItems.length) {
      return true;
    }
    for (let i = currentOverflowItems.length - 1; i >= 0; i -= 1) {
      if (prevOverflowItems[i].title !== currentOverflowItems[i].title) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns true if the top level tab header items have changed in the last update. This check is necessary
   * in the case that a contextual tab is selected, but the tab is removed due to a change in focus, or if a tab is replaced.
   */
  private topLevelHeaderItemsChanged = (
    prevTopLevelItems: IPivotHeader[],
    currentTopLevelItems: IPivotHeader[]
  ): boolean => {
    if (prevTopLevelItems.length !== currentTopLevelItems.length) {
      return true;
    }
    for (let i = prevTopLevelItems.length - 1; i >= 0; i -= 1) {
      if (prevTopLevelItems[i].title !== currentTopLevelItems[i].title) {
        return true;
      }
    }
    return false;
  };

  public focus() {
    if (this.pivot.current) {
      this.pivot.current.focus();
    }
  }

  private getOnTabHeaderActivate = (
    id: string,
    payload?: PivotHeaderExecutePayload
  ) => {
    this.onTabHeaderActivate(id, false /* fullWidthOnSelect */, payload);
  };

  private onTabHeaderActivate = (
    id: string,
    fullWidthOnSelect: boolean,
    payload?: PivotHeaderExecutePayload
  ) => {
    if (this.props.selectedTabId === id) {
      // If already selected, do nothing
      return;
    }

    this.props.onTabHeaderClick(id, payload);
    // If the tab we're moving to is in the overflow well, we can't update the pivot indicator until the newly
    // selected pivot header has been rendered. This is done by rendering those cases in componentDidUpdate
    const incomingTabFromOverflow = this.props.overflowTabHeaders
      ? this.containsTabWithId(this.props.overflowTabHeaders, id)
      : false;
    if (this.pivotBarWrapper.current && !incomingTabFromOverflow) {
      this.onPivotHeaderChanged(id, fullWidthOnSelect);
    }
  };

  private getOnTabHeaderFocus = (id: string) => {
    this.onTabHeaderActivate(id, false /* fullWidthOnSelect */, {
      inputMethod: InputMethod.Keyboard,
    });
  };

  private containsTabWithId = (
    overflowtabs: TabHeader[],
    id: string
  ): boolean => {
    return overflowtabs.some((tab) => tab.id === id);
  };

  private getFullWidthIndicatorMouseEventHandler = (id: string) => {
    if (id === this.props.selectedTabId) {
      this.renderPivotIndicator(id, true /* fullWidthIndicator */);
    }
  };

  private getMouseEventHandler = (id: string) => {
    if (id === this.props.selectedTabId) {
      this.renderPivotIndicator(id, false /* fullWidthIndicator */);
    }
  };

  private onClick = (id: string) => {
    this.renderPivotIndicator(id, true);
  };

  private onOverflowButtonExecute = () => {
    if (this.props.onOverflowButtonExecute) {
      // we need to MenuOpenOnExecuteParameter for a mneu open callback. The given id will never be used.
      this.props.onOverflowButtonExecute({ id: this.noOpFileMenuOpenId });
    }
  };

  public onPivotHeaderChanged = (id: string, fullLength: boolean) => {
    if (
      !this.headerCoordinates[id] ||
      (this.props.overflowTabHeaders &&
        this.containsTabWithId(this.props.overflowTabHeaders, id)) ||
      // This is a workaround for macs where an additional font is downloaded after boot and changes the dimensions in
      // the ribbon. A more permanent fix is tracked by VSO:2661557
      window.navigator.userAgent.indexOf("Macintosh") >= 0
    ) {
      this.setPivotHeaderCoordinates();
    }
    this.renderPivotIndicator(id, fullLength);
  };

  private getTabBoundingRect = (id: string): ClientRect | undefined => {
    const element = this.getTabWrapperElement(id);
    return element ? element.getBoundingClientRect() : undefined;
  };

  private getTabLabelBoundingRect = (id: string): ClientRect | undefined => {
    const element = this.getTabWrapperElement(id);
    const labelElement = element
      ? element.getElementsByClassName(pivotHeaderLabelClassname)[0]
      : undefined;
    return labelElement ? labelElement.getBoundingClientRect() : undefined;
  };

  private getTabWrapperElement = (id: string): Element | null => {
    return (
      this.pivotWrapper.current &&
      this.pivotWrapper.current.querySelector(
        `[id='${id}${pivotHeaderWrapperIDSuffix}']`
      )
    );
  };

  private getSelectedPivotIndicatorValues = (
    id: string,
    fullLength: boolean
  ): PivotIndicatorDOMCoordinates => {
    const rtl = getRTL();
    const tabHeaderRect = fullLength
      ? this.getTabBoundingRect(id)
      : this.getTabLabelBoundingRect(id);
    let position = 0;
    if (tabHeaderRect) {
      const tabHeaderRectSidePosition = !rtl
        ? tabHeaderRect.left
        : tabHeaderRect.right;
      position = !rtl
        ? tabHeaderRectSidePosition - this.positionOffset
        : this.positionOffset - tabHeaderRectSidePosition;
    }
    return {
      position,
      length: tabHeaderRect ? tabHeaderRect.width : 0,
    };
  };

  private setPivotHeaderCoordinates = () => {
    if (this.props.tabCoordinates) {
      this.headerCoordinates = this.props.tabCoordinates;
    } else {
      const clientRect = this.pivotBarWrapper.current?.getBoundingClientRect();
      const inlineStart = getRTL() ? "right" : "left";
      this.positionOffset = clientRect ? clientRect[inlineStart] : 0;

      this.props.topLevelTabHeaders.map((header) => {
        this.headerCoordinates[header.id] = {
          label: this.getSelectedPivotIndicatorValues(header.id, false),
          fullLength: this.getSelectedPivotIndicatorValues(header.id, true),
        };
      });
    }

    this.props.onTabCoordinatesCalculated?.(this.headerCoordinates);
  };

  private getCachedCoordinateValues = (
    id: string,
    fullLength: boolean
  ): PivotIndicatorDOMCoordinates => {
    if (this.headerCoordinates[id]) {
      return fullLength
        ? this.headerCoordinates[id].fullLength
        : this.headerCoordinates[id].label;
    }
    return this.getSelectedPivotIndicatorValues(id, fullLength);
  };
}

export default TabList;
